import { Command, Option } from "clipanion";
import { M8tCommand } from "../lib/m8t-command.js";
import { discoverySwitch, profileSwitch, type SwitchResult } from "../lib/switch.js";
import { listProfiles } from "../lib/profiles.js";
import { readFoundryConfig } from "../lib/foundry-config.js";
import { LocalCliError } from "../lib/errors.js";
import { colors, renderJson, renderKeyValueBlock, resolveOutputMode } from "../lib/output.js";

export class SwitchCommand extends M8tCommand {
  static paths = [["switch"]];
  static usage = Command.Usage({
    description:
      "Re-point local config at another deployment: --subscription <id|name> (discovery) or <profile>.",
  });

  profile = Option.String({ required: false });
  subscription = Option.String("--subscription");
  list = Option.Boolean("--list", false);
  as = Option.String("--as");
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );

    if (this.list) {
      const profiles = await listProfiles();
      const current = await readFoundryConfig();
      if (mode === "json") {
        this.context.stdout.write(
          renderJson({ profiles, currentTenant: current?.tenantId ?? null }) + "\n",
        );
        return 0;
      }
      this.context.stdout.write(
        profiles.length
          ? "saved profiles:\n" + profiles.map((p) => `  ${p}`).join("\n") + "\n"
          : "no saved profiles yet.\n",
      );
      if (current) {
        this.context.stdout.write(colors.dim(`current tenant: ${current.tenantId}`) + "\n");
      }
      return 0;
    }

    let result: SwitchResult;
    if (this.subscription) {
      result = await discoverySwitch(this.subscription, this.as);
    } else if (this.profile) {
      result = await profileSwitch(this.profile, this.as);
    } else {
      throw new LocalCliError({
        code: "SWITCH_NO_TARGET",
        message: "No switch target specified.",
        hint: "Use 'm8t switch --subscription <id|name>' or 'm8t switch <profile>' (list with --list).",
      });
    }

    if (mode === "json") {
      this.context.stdout.write(renderJson(result) + "\n");
      return 0;
    }
    this.context.stdout.write(
      renderKeyValueBlock([
        { key: "tenant", value: result.tenantId },
        { key: "clientId", value: result.clientId },
        { key: "endpoint", value: result.projectEndpoint },
        { key: "subscription", value: result.subscriptionId },
        { key: "saved profile", value: result.snapshot ?? colors.dim("(nothing to snapshot)") },
      ]) + "\n",
    );
    this.context.stdout.write(
      colors.dim("next: run 'm8t doctor' to verify gateway + data-plane access.\n"),
    );
    return 0;
  }
}
